import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Users, ShieldCheck, Radio, Globe2, Zap, Lock, Cpu } from "lucide-react";
import { ASSETS } from "@/data/products";
import SEO from "@/components/SEO";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";

const NETWORK_CARDS = [
  {
    icon: <Globe2 size={20} />,
    title: "Native local access, not roaming",
    text: "In New Zealand, our SIMs connect natively to Spark NZ, 2degrees and One NZ. In Australia, we use the Telstra Direct (Retail) network — not Wholesale — alongside Optus and Vodafone, with strongest signal wins and continuous real data-access monitoring, not a limited wholesale roaming tier.",
  },
  {
    icon: <Zap size={20} />,
    title: "Local Breakouts (LBO) — Auckland & Sydney",
    text: "Data from a New Zealand SIM exits locally in Auckland; data from an Australian SIM exits locally in Sydney. Eliminating the overseas backhaul most global providers rely on keeps latency down to under 40ms — critical for real-time Push-to-Talk, live video, body cams and telemetry.",
  },
  {
    icon: <ShieldCheck size={20} />,
    title: "Not a permanent-roaming risk",
    text: "Many international roaming and travel SIMs get disconnected or throttled after roughly 60–90 days on a foreign network under standard fair-use policies. Because our SIMs run on native, locally-agreed networks rather than a roaming profile, PTT NZ deployments aren't exposed to that risk.",
  },
  {
    icon: <Cpu size={20} />,
    title: "LTE-M for industrial IoT",
    text: "Our SIMs support LTE-M (Cat-M1) low-power wide-area connectivity with automatic multi-network failover built into the hardware layer — devices switch networks without draining the battery, ideal for sensors, meters and remote telemetry.",
  },
  {
    icon: <Lock size={20} />,
    title: "Data sovereignty & compliance",
    text: "Local breakouts mean sensitive corporate, enterprise and government data stays within domestic borders in both New Zealand and Australia — supporting regulatory compliance frameworks in each country.",
  },
  {
    icon: <Globe2 size={20} />,
    title: "Non-steered network core",
    text: "PTT NZ SIMs run on an independent core network profile — not a standard consumer multi-IMSI profile that forces a mechanical reset every time you switch towers. Selection is fully non-steered: your device locks onto whichever network has the mathematically strongest signal, organically, with no artificial carrier throttling or intentional prioritisation.",
  },
  {
    icon: <ShieldCheck size={20} />,
    title: "Government & public safety resilience",
    text: "The same native, non-steered architecture is available to government agencies either as a complete primary network or as a complementary resilience layer alongside an existing one — with domestic NZ-hosted management and zero-firmware provisioning.",
    link: { to: "/government-public-safety", label: "See Government & Public Safety" },
  },
];

const FAQS = [
  {
    q: "Does PTT NZ roam onto Australian networks, or do I get local access?",
    a: "PTT NZ SIMs use native local network agreements in Australia — including the Telstra Direct (Retail) network, Optus and Vodafone — rather than a wholesale roaming tier. That means local pricing, local support through our Australian Authorised Reseller network, and a genuine local breakout, not a roaming profile bolted onto a New Zealand SIM.",
  },
  {
    q: "What is a Local Breakout (LBO) and why does it matter?",
    a: "A Local Breakout means your data exits to the internet inside the country you're in — Auckland for New Zealand SIMs, Sydney for Australian SIMs — instead of being routed overseas through a distant data centre. That keeps latency under 40ms, which matters for real-time Push-to-Talk, live video streaming, body cameras and telemetry.",
  },
  {
    q: "Why do some international roaming SIMs stop working after 60–90 days?",
    a: "Many roaming and travel eSIM providers rely on 'permanent roaming' — parking a foreign SIM long-term on someone else's local network. Most carriers worldwide enforce fair-use policies that disconnect or throttle SIMs left in permanent roaming for around 60–90 days, since they were never designed for long-term use outside their home country. PTT NZ SIMs connect to native, locally-agreed networks in both New Zealand and Australia rather than a roaming profile, so they aren't exposed to that disconnection risk.",
  },
  {
    q: "Does PTT NZ support LTE-M for IoT and industrial telemetry?",
    a: "Yes. PTT NZ SIMs support LTE-M (Cat-M1) low-power wide-area connectivity with automatic multi-network failover built into the hardware layer, so devices switch networks without draining the battery — ideal for sensors, meters and remote telemetry.",
  },
  {
    q: "How does PTT NZ's Pay As You Go data pricing work?",
    a: "PTT NZ Pay As You Go plans combine a low flat monthly SIM access fee with pure per-MB consumption pricing — no bundles to outgrow or overage fees to worry about. In New Zealand, Cellular IoT SIM access starts from $2.00 NZD per SIM per month with data from $0.06 NZD/MB. In Australia, Cellular IoT SIM access starts from $1.60 AUD per SIM per month with data from $0.048 AUD/MB. Pricing scales down further for high-volume fleets. Exact current pricing for every plan is always shown live on our Plans page.",
  },
  {
    q: "Can PTT NZ handle large enterprise or fleet deployments across NZ and Australia?",
    a: "Yes. Beyond standard retail and IoT plans, we offer bespoke enterprise and utility volume pricing frameworks engineered to beat rigid single-carrier contracts, backed by direct wholesale carrier relationships across both countries.",
  },
  {
    q: "Do PTT NZ SIMs use network steering?",
    a: "No. PTT NZ SIMs are fully non-steered at the core network level. Rather than a standard consumer multi-IMSI profile that mechanically resets every time you switch towers, our independent core architecture lets the hardware choose the mathematically strongest signal tier available — Spark, One NZ or 2degrees in New Zealand; Telstra Direct (Retail), Optus or Vodafone in Australia — organically, based purely on RF signal quality, with no artificial carrier throttling or prioritisation.",
  },
  {
    q: "What happens if a cell tower shows full signal but has no data access?",
    a: "PTT NZ SIMs and eSIMs run a smart applet that continuously checks real data access, not just signal strength. A tower can show full bars while its backhaul is down or it's mid-outage — plenty of signal, but no working data path. Our applet detects that and actively swaps to the next available network until it finds one that's actually passing data, so devices never sit 'stuck' on a strong-looking but dead connection.",
  },
];

const About = () => (
  <div data-testid="about-page">
    <SEO
      path="/about"
      title="About PTT NZ — Christchurch Multi-Network MVNO"
      description="Christchurch-based multi-network MVNO — SIMs, eSIMs, IoT & critical failover for enterprise, government and RV travellers across NZ, Australia & 190+ countries."
      keywords="PTT NZ Limited company, Christchurch telecommunications company, mission critical connectivity provider New Zealand, mission critical connectivity provider Australia, locally owned NZ telco, multi-network SIM company, local breakout Auckland Sydney, LTE-M IoT New Zealand Australia, government connectivity NZ AU, enterprise fleet SIM NZ AU, business backup internet NZ"
      jsonLd={{
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQS.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
      }}
    />
    <section className="bg-[var(--ptt-paper)] border-b border-[var(--ptt-line)]">
      <div className="max-w-7xl mx-auto px-5 lg:px-10 pt-16 pb-14">
        <div className="eyebrow">About PTT NZ</div>
        <h1 className="font-display text-5xl lg:text-6xl font-bold mt-3 leading-[1.02] tracking-tight max-w-3xl">
          Nationwide reach. <span className="text-[var(--ptt-teal-deep)]">Locally owned.</span> Based in Christchurch.
        </h1>
        <p className="text-[var(--ptt-muted)] mt-5 max-w-2xl">
          PTT NZ Limited is a Christchurch based specialist in mission-critical Push-to-Talk over Cellular, on-body cameras, IoT hardware and multi-network connectivity nationwide. We exist because critical comms shouldn&apos;t be tied to a single carrier — especially in a country as varied as Aotearoa and with disasters becoming more regular. Services can not be fully reliant on direct to cell satellite as this itself is dependent on ground stations and clear sight to the sky.
        </p>
      </div>
    </section>

    <section className="max-w-7xl mx-auto px-5 lg:px-10 py-16 grid lg:grid-cols-12 gap-12 items-start">
      <div className="lg:col-span-7">
        <div className="eyebrow">Our story</div>
        <h2 className="font-display text-3xl lg:text-4xl font-bold mt-2 leading-tight">A platform built for how Kiwi teams really work.</h2>
        <div className="mt-5 space-y-4 text-[var(--ptt-ink-soft)] leading-relaxed">
          <p>From the West Coast to the Coromandel — and beyond NZ shores — single-carrier coverage has limits. PTT NZ has taken on the challenge to fix that with a multi-network SIM/eSIM platform that connects natively to Spark NZ, 2degrees and One NZ, with genuine native local access across Australia&apos;s major networks — not a roaming bolt-on — plus 190+ countries globally via international roaming partners. Paired with hardware tough enough for the field.</p>
          <p>Today we support utilities, construction, transport, security and primary industries with PoC radios, evidence-grade body cameras, IoT terminals and broadband redundancy — all backed by a Christchurch-based team that picks up the phone.</p>
        </div>
        <div className="grid sm:grid-cols-3 gap-5 mt-10">
          {[
            { icon: <Radio size={20} />, k: "Mission-critical", v: "Comms-first" },
            { icon: <Users size={20} />, k: "Locally owned", v: "Christchurch HQ" },
            { icon: <ShieldCheck size={20} />, k: "Multi-network", v: "NZ · AU · Global" },
          ].map((x) => (
            <div key={x.k} className="bg-white border border-[var(--ptt-line)] p-5">
              <div className="w-10 h-10 grid place-items-center bg-[var(--ptt-teal)]/15">{x.icon}</div>
              <div className="font-display text-lg font-bold mt-3">{x.k}</div>
              <div className="font-mono-tech text-[10px] uppercase tracking-widest text-[var(--ptt-muted)] mt-1">{x.v}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="lg:col-span-5">
        <div className="relative border border-[var(--ptt-ink)] overflow-hidden aspect-[4/5]">
          <img src={ASSETS.hero} alt="PTT NZ in the field — backcountry NZ" className="w-full h-full object-cover" />
          <div className="absolute bottom-4 left-4 right-4 bg-[var(--ptt-ink)]/85 backdrop-blur-sm p-4 border border-white/15">
            <div className="font-mono-tech text-[10px] tracking-widest uppercase text-white/60">Field-tested</div>
            <div className="text-white text-sm font-semibold mt-1">From Stewart Island to the Far North — and out across Australia and 190+ countries — the same SIM, the same handset.</div>
          </div>
        </div>
        <div className="mt-6 p-5 border border-[var(--ptt-line)] bg-white flex items-start gap-3">
          <MapPin size={18} className="text-[var(--ptt-teal-deep)] mt-0.5" />
          <div>
            <div className="font-display font-bold text-sm">Christchurch HQ</div>
            <div className="text-sm text-[var(--ptt-muted)]">14 Hazeldean Road, Addington, Christchurch 8024</div>
          </div>
        </div>
      </div>
    </section>

    <section id="network-architecture" className="bg-[var(--ptt-paper)] border-y border-[var(--ptt-line)]" data-testid="about-network-architecture">
      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-20">
        <div className="max-w-2xl">
          <div className="eyebrow">Network architecture</div>
          <h2 className="font-display text-3xl lg:text-4xl font-bold mt-2 leading-tight">Why we&apos;re not a roaming SIM.</h2>
          <p className="mt-4 text-[var(--ptt-ink-soft)] leading-relaxed">
            Local network agreements and native Local Breakouts in Auckland and Sydney set PTT NZ apart from providers that simply resell overseas roaming.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
          {NETWORK_CARDS.map((c) => (
            <div key={c.title} className="bg-white border border-[var(--ptt-line)] p-6" data-testid={`network-card-${c.title.replace(/\s+/g, '-').toLowerCase().replace(/[^a-z0-9-]/g, '')}`}>
              <div className="w-10 h-10 grid place-items-center bg-[var(--ptt-teal)]/15">{c.icon}</div>
              <div className="font-display text-base font-bold mt-4 leading-snug">{c.title}</div>
              <p className="text-sm text-[var(--ptt-muted)] mt-2 leading-relaxed">{c.text}</p>
              {c.link && (
                <Link to={c.link.to} data-testid="network-card-government-link" className="mt-3 pt-3 border-t border-[var(--ptt-line)] text-sm font-semibold inline-flex items-center gap-1.5 signal-underline">
                  {c.link.label} <ArrowRight size={13} />
                </Link>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>

    <section id="faq" className="max-w-4xl mx-auto px-5 lg:px-10 py-16 lg:py-20" data-testid="about-faq-section">
      <div className="eyebrow">FAQ</div>
      <h2 className="font-display text-3xl lg:text-4xl font-bold mt-2 leading-tight">Common questions.</h2>
      <Accordion type="single" collapsible className="mt-8" data-testid="about-faq-accordion">
        {FAQS.map((f, i) => (
          <AccordionItem key={f.q} value={`faq-${i}`} className="border-[var(--ptt-line)]" data-testid={`faq-item-${i}`}>
            <AccordionTrigger data-testid={`faq-trigger-${i}`} className="font-display text-base font-bold text-left hover:no-underline">{f.q}</AccordionTrigger>
            <AccordionContent data-testid={`faq-content-${i}`} className="text-[var(--ptt-muted)] leading-relaxed">{f.a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </section>

    <section className="max-w-7xl mx-auto px-5 lg:px-10 mb-24">
      <div className="bg-[var(--ptt-ink)] text-white p-10 lg:p-14 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
        <div>
          <div className="eyebrow text-white/50">Talk to us</div>
          <h3 className="font-display text-3xl lg:text-4xl font-bold mt-2">Let&apos;s spec your next deployment.</h3>
        </div>
        <Link to="/contact" data-testid="about-cta-contact" className="ptt-button px-6 py-3.5 text-sm font-bold inline-flex items-center gap-2">Start a conversation <ArrowRight size={16} /></Link>
      </div>
    </section>
  </div>
);

export default About;
